function getErrorCode(error) {
  return String(error?.error || error?.code || error?.name || "").trim();
}

function getErrorMessage(error) {
  if (typeof error === "string") return error.trim();
  return String(error?.message || "").trim();
}

export function normalizeLoginError(errorMessage) {
  const code = getErrorCode(errorMessage);
  const message = getErrorMessage(errorMessage);

  if (!message) {
    return "Ocorreu um erro ao tentar fazer login. Tente novamente.";
  }

  if (code === "rate_limit_exceeded") {
    return "Muitas tentativas. Tente novamente mais tarde.";
  }

  if (code === "missing_required_fields" || code === "validation_error") {
    return "Preencha todos os campos obrigatórios.";
  }

  if (code === "invalid_credentials") {
    return "Usuário ou senha inválidos.";
  }

  const lowerMessage = message.toLowerCase();

  if (
    lowerMessage.includes("invalid credential") ||
    lowerMessage.includes("user does not exist") ||
    lowerMessage.includes("incorrect username or password") ||
    lowerMessage.includes("not authorized") ||
    lowerMessage.includes("unauthorized")
  ) {
    return "Usuário ou senha inválidos.";
  }

  if (message.includes("Field") && message.includes("is required")) {
    return "Preencha todos os campos obrigatórios.";
  }

  const knownMessages = {
    "Invalid credentials": "Credenciais inválidas. Por favor tente novamente.",
    "User does not exist.": "Credenciais inválidas. Por favor tente novamente.",
    "You need to provide your password.": "Você precisa fornecer sua senha.",
    "Please provide the email OTP code sent to your email.":
      "Insira o código enviado para seu e-mail.",
    "Please provide the MFA code from your authenticator app.":
      "Insira o código MFA do seu aplicativo autenticador.",
    "Please provide a new password.": "Você precisa fornecer uma nova senha.",
    "An unexpected error occurred":
      "Ocorreu um erro inesperado. Tente novamente mais tarde.",
  };

  return (
    knownMessages[message] ||
    "Ocorreu um erro ao tentar fazer login. Tente novamente."
  );
}

export function normalizeLoginStateMessage(stateMessage) {
  const message = String(stateMessage || "").trim();

  if (!message) return "";

  if (
    !/invalid|error|password|user|credential|required|unexpected|incorrect|authorized/i.test(
      message,
    )
  ) {
    return message;
  }

  return normalizeLoginError(message);
}

export function isInvalidCredentialsMessage(message) {
  return [
    "Usuário ou senha inválidos.",
    "Credenciais inválidas. Por favor tente novamente.",
  ].includes(message);
}

export function getLoginUsernameHelperText(message) {
  if (message === "Por favor, preencha todos os campos.") {
    return "Informe seu e-mail ou usuário.";
  }

  return message;
}

export function getLoginPasswordHelperText(message) {
  if (message === "Por favor, preencha todos os campos.") {
    return "Informe sua senha.";
  }

  return message;
}

export function normalizeSignupError(errorMessage) {
  const code = getErrorCode(errorMessage);
  const message = getErrorMessage(errorMessage);
  const lowerMessage = message.toLowerCase();

  if (!message) {
    return "Não foi possível criar sua conta. Verifique os dados e tente novamente.";
  }

  if (code === "missing_required_fields" || code === "validation_error") {
    return "Preencha todos os campos.";
  }
  if (code === "rate_limit_exceeded") {
    return "Muitas solicitações. Tente novamente mais tarde.";
  }
  if (code === "user_already_exists") {
    return "E-mail ou nome de usuário já existem.";
  }
  if (code === "email_not_verified") {
    return "Este e-mail ainda não foi verificado.";
  }
  if (code === "weak_password") {
    return "A senha precisa ter no mínimo 8 caracteres, com letra maiúscula, letra minúscula, número e caractere especial.";
  }

  if (message.includes("Field")) return "Preencha todos os campos.";
  if (/missing required|required|obrigat[oó]rio/i.test(message)) return "Preencha todos os campos.";
  if (/too many|rate_limit|limit|muitas/i.test(message)) {
    return "Muitas solicitações. Tente novamente mais tarde.";
  }
  if (message === "Username already exists") return "Esse nome de usuário já existe.";
  if (message === "Email already exists") return "Esse e-mail já foi cadastrado.";
  if (message === "Email or username already exists") {
    return "E-mail ou nome de usuário já existem.";
  }
  if (/already exists|user_already_exists/i.test(message)) {
    return "E-mail ou nome de usuário já existem.";
  }
  if (message === "Invalid credentials") return "Credenciais inválidas.";

  if (
    lowerMessage.includes("weak_password") ||
    lowerMessage.includes("password did not conform") ||
    lowerMessage.includes("not long enough") ||
    lowerMessage.includes("senha muito curta") ||
    lowerMessage.includes("muito curta") ||
    lowerMessage.includes("senha fraca")
  ) {
    return "A senha precisa ter no mínimo 8 caracteres, com letra maiúscula, letra minúscula, número e caractere especial.";
  }

  if (
    message === "Password must be at least 8 characters long" ||
    lowerMessage.includes("password must be at least 8") ||
    lowerMessage.includes("pelo menos 8")
  ) {
    return "A senha precisa ter no mínimo 8 caracteres.";
  }

  if (
    message === "Password must contain at least 1 number" ||
    lowerMessage.includes("password must contain at least 1 number") ||
    lowerMessage.includes("número")
  ) {
    return "A senha precisa conter pelo menos 1 número.";
  }

  if (
    message === "Password must contain at least 1 uppercase letter" ||
    lowerMessage.includes("password must contain at least 1 uppercase") ||
    lowerMessage.includes("maiúscula")
  ) {
    return "A senha precisa conter pelo menos 1 letra maiúscula.";
  }

  if (
    message === "Password must contain at least 1 lowercase letter" ||
    lowerMessage.includes("password must contain at least 1 lowercase") ||
    lowerMessage.includes("minúscula")
  ) {
    return "A senha precisa conter pelo menos 1 letra minúscula.";
  }

  if (message === "User does not exist.") return "Este usuário não existe.";

  return "Não foi possível criar sua conta. Verifique os dados e tente novamente.";
}

export function normalizeForgotPasswordError(errorMessage) {
  const code = getErrorCode(errorMessage);
  const message = getErrorMessage(errorMessage);

  if (!message) return "Não foi possível solicitar a recuperação de senha.";
  if (code === "missing_required_fields" || code === "validation_error") {
    return "Preencha o campo de e-mail ou usuário.";
  }
  if (code === "rate_limit_exceeded") {
    return "Muitas solicitações. Tente novamente mais tarde.";
  }
  if (/inv[aá]lido|invalid|404/i.test(message)) {
    return "Esse e-mail ou usuário é inválido.";
  }
  if (/muitas|429|limit|too many/i.test(message)) {
    return "Muitas solicitações. Tente novamente mais tarde.";
  }

  return "Não foi possível solicitar a recuperação de senha.";
}

export function normalizePasswordChangeError(errorMessage) {
  const code = getErrorCode(errorMessage);
  const message = getErrorMessage(errorMessage);

  if (!message) return "Ocorreu um erro ao alterar a senha.";
  if (code === "missing_required_fields" || code === "validation_error") {
    return "Por favor, preencha todos os campos.";
  }
  if (code === "rate_limit_exceeded") {
    return "Muitas solicitações. Tente novamente mais tarde.";
  }
  if (code === "invalid_or_expired_token") {
    return "Link de recuperação inválido ou expirado.";
  }
  if (code === "password_mismatch") {
    return "As senhas não coincidem.";
  }
  if (code === "weak_password") {
    return "A senha precisa ter no mínimo 8 caracteres, com letra maiúscula, letra minúscula, número e caractere especial.";
  }
  if (/token|link/i.test(message)) return "Link de recuperação inválido ou expirado.";
  if (/muitas|429|limit|too many/i.test(message)) {
    return "Muitas solicitações. Tente novamente mais tarde.";
  }
  if (/senha muito curta|muito curta|senha fraca|weak_password|not long enough/i.test(message)) {
    return "A senha precisa ter no mínimo 8 caracteres, com letra maiúscula, letra minúscula, número e caractere especial.";
  }
  if (/senha|password/i.test(message)) return message;

  return "Ocorreu um erro ao alterar a senha.";
}

export function normalizeResetPasswordError(error) {
  const code = error?.name || error?.code || "";

  switch (code) {
    case "CodeMismatchException":
      return "Código inválido. Verifique o e-mail e tente novamente.";
    case "ExpiredCodeException":
      return "Código expirado. Reenvie o código e tente novamente.";
    case "UserNotFoundException":
      return "Usuário não encontrado.";
    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Muitas tentativas. Tente novamente mais tarde.";
    default:
      return "Erro ao redefinir a senha. Tente novamente.";
  }
}
